/**
 * The recorded conversation, and how it finds its way to a job.
 *
 * A Plaud recorder knows three things about a recording: when it started, how
 * long it ran, and which device made it. It does not know where it was, and it
 * has never heard of a customer. So the recording cannot say which job it
 * belongs to, and something else has to.
 *
 * That something is CompanyCam. Reps photograph every appointment already, and
 * a photograph carries the two facts a recording is missing — a time and a pair
 * of coordinates — on a project that is already tied to a job. A run of
 * photographs by one rep at one project becomes a *visit*: "this person was at
 * this house between these two times". A recording made inside one of those
 * windows, by that rep's device, is a recording of that visit.
 *
 * Everything here is arithmetic on times. It lives in core rather than in the
 * Edge Function (CLAUDE.md rule 5) because "which job was this" is the whole
 * business rule of the feature, and a rule that only exists inside a serverless
 * function is a rule nobody can test and an agent can bypass.
 *
 * ADR 0064 carries the reasoning; the numbers below are the part that will
 * actually get tuned, so they are one object rather than scattered literals.
 */

/**
 * The constants the matching turns on.
 *
 * These are first guesses from the specification, not measurements. They are
 * meant to be adjusted once a week of real recordings has been hand-checked,
 * and the tests below pin the *behaviour* at each threshold rather than the
 * numbers themselves, so moving one is a deliberate act with a visible diff.
 */
export const CONVERSATION_RULES = {
  /**
   * How long a gap between photographs still counts as the same visit.
   *
   * An inspection is photographs on the roof, then a gap, then the
   * conversation at the kitchen table. Forty-five minutes keeps those as one
   * visit; anything longer is the rep having driven somewhere.
   */
  visitGapMinutes: 45,
  /**
   * How far outside the photographs a recording may fall and still belong.
   *
   * The recorder is running before the first photograph and after the last —
   * the doorstep conversation is the part nobody photographs.
   */
  visitPadMinutes: 30,
  /** The same allowance for a scheduled inspection, which is a plan, not proof. */
  calendarPadMinutes: 45,
  /** Below this, a recording is a pocket press, not a conversation. */
  shortClipSeconds: 90,
  /** Where the "started at about the right time" score reaches zero. */
  proximityFalloffMinutes: 90,
  /** How much of the score is "the recording happened during the visit". */
  overlapWeight: 0.7,
  /** ... and how much is "it started when the visit started". */
  proximityWeight: 0.3,
  /**
   * The ceiling on a calendar match. An appointment says somebody intended to
   * be there; a photograph says they were. The intention never scores as high.
   */
  calendarCeiling: 0.6,
  /** Below this, nothing is claimed automatically. */
  acceptScore: 0.6,
  /**
   * How far ahead of the runner-up the winner must be. Two houses back to back
   * produce two plausible visits, and the honest answer there is to ask.
   */
  acceptMargin: 0.25,
  /** How many choices a person is offered before it stops being a choice. */
  maxCandidates: 3,
} as const;

const MINUTE_MS = 60_000;

/** A recording, reduced to the two facts matching uses. */
export interface RecordingWindow {
  startedAt: string | Date;
  durationMs: number;
}

/** A window of time somebody was somewhere. */
export interface VisitWindow {
  jobId: string;
  startedAt: string | Date;
  endedAt: string | Date;
}

/** One possible answer, with the reason it is possible. */
export interface MatchCandidate {
  jobId: string;
  score: number;
  reason: string;
}

/** What matching decided, ready to be written onto the conversation row. */
export interface MatchOutcome {
  status: "matched" | "ambiguous" | "unassigned" | "short_clip";
  jobId: string | null;
  matchMethod: "companycam" | "calendar" | null;
  matchConfidence: number | null;
  candidates: MatchCandidate[];
}

function ms(value: string | Date): number {
  return value instanceof Date ? value.getTime() : new Date(value).getTime();
}

/** Start and end of a recording, in milliseconds. */
export function recordingBounds(recording: RecordingWindow): {
  start: number;
  end: number;
} {
  const start = ms(recording.startedAt);
  return { start, end: start + Math.max(0, recording.durationMs) };
}

/** True for the button presses and the tests, which nobody wants matched. */
export function isShortClip(durationMs: number): boolean {
  return durationMs < CONVERSATION_RULES.shortClipSeconds * 1000;
}

/* ------------------------------------------------- building a visit --- */

/**
 * Does this photograph belong to that visit, or start a new one?
 *
 * True when the photograph falls inside the visit or within the gap of either
 * end of it. Both ends matter because photographs do not arrive in order: the
 * nightly backfill catches ones the webhook missed, and an earlier photograph
 * has to be able to move the start of a visit backwards.
 */
export function photoJoinsVisit(
  visit: { startedAt: string | Date; endedAt: string | Date },
  capturedAt: string | Date,
): boolean {
  const at = ms(capturedAt);
  const gap = CONVERSATION_RULES.visitGapMinutes * MINUTE_MS;
  return at >= ms(visit.startedAt) - gap && at <= ms(visit.endedAt) + gap;
}

/** A visit as the photographs have left it so far. */
export interface VisitAccumulator {
  startedAt: string;
  endedAt: string;
  photoCount: number;
  centroidLat: number | null;
  centroidLng: number | null;
}

/**
 * The visit after one more photograph.
 *
 * The centroid is a running mean, so a visit does not have to keep every
 * photograph to know roughly where it happened. A photograph without
 * coordinates — CompanyCam allows it — moves the times and the count and
 * leaves the position alone rather than dragging it toward zero.
 */
export function visitWithPhoto(
  visit: VisitAccumulator,
  photo: {
    capturedAt: string | Date;
    lat?: number | null;
    lng?: number | null;
  },
): VisitAccumulator {
  const at = ms(photo.capturedAt);
  const started = Math.min(ms(visit.startedAt), at);
  const ended = Math.max(ms(visit.endedAt), at);

  const hasPosition =
    typeof photo.lat === "number" && typeof photo.lng === "number";
  const placed = visit.centroidLat !== null && visit.centroidLng !== null;
  const weight = placed ? visit.photoCount : 0;

  return {
    startedAt: new Date(started).toISOString(),
    endedAt: new Date(ended).toISOString(),
    photoCount: visit.photoCount + 1,
    centroidLat: hasPosition
      ? ((visit.centroidLat ?? 0) * weight + (photo.lat as number)) /
        (weight + 1)
      : visit.centroidLat,
    centroidLng: hasPosition
      ? ((visit.centroidLng ?? 0) * weight + (photo.lng as number)) /
        (weight + 1)
      : visit.centroidLng,
  };
}

/** The first visit a photograph makes when it joins none. */
export function visitFromPhoto(photo: {
  capturedAt: string | Date;
  lat?: number | null;
  lng?: number | null;
}): VisitAccumulator {
  const at = new Date(ms(photo.capturedAt)).toISOString();
  return visitWithPhoto(
    {
      startedAt: at,
      endedAt: at,
      photoCount: 0,
      centroidLat: null,
      centroidLng: null,
    },
    photo,
  );
}

/* ------------------------------------------------------- the scoring --- */

/** How much of the recording happened inside the window. 0 to 1. */
export function overlapFraction(
  recording: RecordingWindow,
  window: { start: number; end: number },
): number {
  const { start, end } = recordingBounds(recording);
  const span = end - start;
  const shared = Math.min(end, window.end) - Math.max(start, window.start);
  // A zero-length recording either sits inside the window or does not; there is
  // no fraction of nothing.
  if (span <= 0) return shared >= 0 ? 1 : 0;
  return Math.max(0, Math.min(1, shared / span));
}

/**
 * How close the recording started to the visit starting. 1 at the same
 * minute, 0 an hour and a half either side.
 */
export function proximityScore(
  recording: RecordingWindow,
  visitStart: string | Date,
): number {
  const apart = Math.abs(recordingBounds(recording).start - ms(visitStart));
  const falloff = CONVERSATION_RULES.proximityFalloffMinutes * MINUTE_MS;
  return Math.max(0, 1 - apart / falloff);
}

/**
 * What a CompanyCam visit is worth as an answer.
 *
 * Mostly "did the recording happen while the rep was there", with a little of
 * "did it start when they arrived" — which is what separates two visits that
 * both overlap a long recording.
 */
export function scoreVisit(
  recording: RecordingWindow,
  visit: VisitWindow,
): number {
  const pad = CONVERSATION_RULES.visitPadMinutes * MINUTE_MS;
  const window = {
    start: ms(visit.startedAt) - pad,
    end: ms(visit.endedAt) + pad,
  };
  const score =
    overlapFraction(recording, window) * CONVERSATION_RULES.overlapWeight +
    proximityScore(recording, visit.startedAt) *
      CONVERSATION_RULES.proximityWeight;
  return round(score);
}

/**
 * What a scheduled inspection is worth.
 *
 * Capped, because an appointment is a plan. The rep may have been rained off,
 * gone somewhere else first, or had the customer cancel at the door, and the
 * calendar would say the same thing in every case.
 */
export function scoreAppointment(
  recording: RecordingWindow,
  inspectionAt: string | Date,
  appointmentMinutes = 60,
): number {
  const pad = CONVERSATION_RULES.calendarPadMinutes * MINUTE_MS;
  const start = ms(inspectionAt);
  const window = {
    start: start - pad,
    end: start + appointmentMinutes * MINUTE_MS + pad,
  };
  return round(
    overlapFraction(recording, window) * CONVERSATION_RULES.calendarCeiling,
  );
}

function round(score: number): number {
  return Math.round(Math.max(0, Math.min(1, score)) * 1000) / 1000;
}

/* ------------------------------------------------------ the decision --- */

/**
 * One recording, every candidate, one answer.
 *
 * The winner has to be good enough *and* clearly better than the runner-up.
 * Both halves matter: the first stops a weak guess becoming a fact, and the
 * second is the two-houses-in-an-afternoon case, where the top two scores are
 * close and the only honest answer is to hand it to a person.
 *
 * Nothing here is final. Every automatic answer arrives on the screen marked
 * as a guess until somebody presses Verify, and a person reassigning a
 * recording overrules all of this without argument.
 */
export function decideMatch(
  recording: RecordingWindow,
  candidates: MatchCandidate[],
  method: "companycam" | "calendar",
): MatchOutcome {
  if (isShortClip(recording.durationMs)) {
    return {
      status: "short_clip",
      jobId: null,
      matchMethod: null,
      matchConfidence: null,
      candidates: [],
    };
  }

  const ranked = [...candidates]
    .filter((candidate) => candidate.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, CONVERSATION_RULES.maxCandidates);

  if (ranked.length === 0) {
    return {
      status: "unassigned",
      jobId: null,
      matchMethod: null,
      matchConfidence: null,
      candidates: [],
    };
  }

  const winner = ranked[0]!;
  const runnerUp = ranked[1];
  const clear =
    winner.score >= CONVERSATION_RULES.acceptScore &&
    (!runnerUp ||
      winner.score - runnerUp.score >= CONVERSATION_RULES.acceptMargin);

  if (clear) {
    return {
      status: "matched",
      jobId: winner.jobId,
      matchMethod: method,
      matchConfidence: winner.score,
      candidates: ranked,
    };
  }

  return {
    status: "ambiguous",
    jobId: null,
    matchMethod: null,
    matchConfidence: null,
    candidates: ranked,
  };
}

/**
 * What the screen says about a conversation's job.
 *
 * A rep should never have to work out whether the job on the row was decided
 * by a person or by arithmetic — the same rule the sends screen already
 * follows. Anything derived says so until somebody confirms it.
 */
export function matchNotice(conversation: {
  status: string;
  matchMethod?: string | null;
  reviewedAt?: string | null;
}): string | null {
  if (conversation.reviewedAt) return null;
  switch (conversation.status) {
    case "matched":
      return conversation.matchMethod === "manual"
        ? null
        : "Auto-matched — verify";
    case "ambiguous":
      return "More than one job fits — pick one";
    case "unassigned":
      return "No job found — assign it";
    case "short_clip":
      return "Too short to match";
    default:
      return null;
  }
}
